/**
 * Keeps `free_trial_history_tiers` aligned with the billing dev override.
 *
 * The tier/seat/trial patch merges on the billing-info read, so a refetch
 * can't wipe it. Trial history lives on the user query, so this re-applies
 * after every user fetch while an override is active and the user is not trialing.
 * Mounted from `DevToolsSidebar`, which stays up when the billing tab unmounts.
 */
import { GET_USER } from '@heidi/workspace/contracts';
import { useQueryClient } from '@tanstack/react-query';
import { useAtom } from 'jotai';
import { useEffect, useRef } from 'react';

import {
  billingDevOverrideAtom,
  trialHistoryTiersForAvailability,
} from '@heidi/commerce/data';

type UserWithTrialHistory = {
  free_trial_history_tiers?: readonly string[] | null;
};

type TrialHistoryCache = {
  getQueryData: (queryKey: readonly unknown[]) => UserWithTrialHistory | undefined;
  setQueryData: (
    queryKey: readonly unknown[],
    updater: (old: UserWithTrialHistory | undefined) => UserWithTrialHistory | undefined
  ) => void;
};

function trialHistoryMatches(
  current: readonly string[] | null | undefined,
  desired: readonly string[]
) {
  const history = current ?? [];
  return (
    history.length === desired.length &&
    desired.every((tier, index) => history[index] === tier)
  );
}

export function applyBillingDevTrialHistory(
  queryClient: TrialHistoryCache,
  trialAvailable: boolean
) {
  const desired = trialHistoryTiersForAvailability(trialAvailable);
  const cached = queryClient.getQueryData([GET_USER]);
  // setQueryData notifies the cache even when the updater returns the same
  // reference, so a no-op write re-enters the subscriber and loops.
  if (cached == null || trialHistoryMatches(cached.free_trial_history_tiers, desired)) {
    return;
  }
  queryClient.setQueryData([GET_USER], (old) => {
    if (old == null || trialHistoryMatches(old.free_trial_history_tiers, desired)) {
      return old;
    }
    return { ...old, free_trial_history_tiers: desired };
  });
}

export function useSyncBillingDevTrialHistory() {
  const [override, setOverride] = useAtom(billingDevOverrideAtom);
  const queryClient = useQueryClient();
  const patchedRef = useRef(false);
  const form = override?.form;
  const shouldPatch = Boolean(form && !form.trialing);
  const trialAvailable = form?.trialAvailable ?? true;

  useEffect(() => {
    if (override && !override.form) {
      setOverride(null);
    }
  }, [override, setOverride]);

  useEffect(() => {
    if (!shouldPatch) {
      if (patchedRef.current) {
        patchedRef.current = false;
        void queryClient.invalidateQueries({ queryKey: [GET_USER] });
      }
      return;
    }

    patchedRef.current = true;
    const apply = () => applyBillingDevTrialHistory(queryClient, trialAvailable);
    apply();
    return queryClient.getQueryCache().subscribe((event) => {
      if (event.type !== 'updated') return;
      const [head] = event.query.queryKey as readonly unknown[];
      if (head !== GET_USER) return;
      apply();
    });
  }, [queryClient, shouldPatch, trialAvailable]);
}
